import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, MessageSquare, FileText } from "lucide-react";
import { openAuthed } from "@/lib/billingApi";
import { useBT } from "@/i18n/billingText";
import { getMyProcessDetail } from "@/lib/portalApi";
import { ProgressBar } from "@/components/account/ProgressBar";
import { DocumentVault } from "@/components/account/DocumentVault";
import { StatusBadge } from "@/components/portal/StatusBadge";
import { formatDateTime } from "@/components/portal/portalConstants";
import { MessageThread } from "@/components/common/MessageThread";
import { getMyMessages, sendMyMessageFile, markAccountRead } from "@/lib/portalApi";
import { useAuth } from "@/context/AuthContext";

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="text-slate-100 text-right break-words">{value || "—"}</span>
  </div>
);

export const CustomerProcessDetail = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { id } = useParams();
  const { bt } = useBT();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await getMyProcessDetail(id);
      setData(d);
      setDocuments(d.documents || []);
    } catch (e) {
      /* handled */
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="grid place-items-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <button onClick={() => navigate("/account")} className="text-venka-green-light">
        {t("account.detail.back")}
      </button>
    );
  }

  const { process, process_type, audit_logs } = data;

  return (
    <div className="space-y-6" data-testid="customer-process-detail">
      <button onClick={() => navigate("/account")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="detail-back">
        <ArrowLeft className="h-4 w-4" /> {t("account.detail.back")}
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{process_type?.name}</h1>
          <p className="text-sm text-slate-400">
            {process?.country_destination || "—"} · {t("account.detail.reference")}: <span className="font-mono text-xs">{process?.reference || process?.id?.slice(0, 8)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => openAuthed(`/account/processes/${process.id}/confirmation.pdf`).catch(() => {})}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-200 hover:border-venka-green" data-testid="my-case-confirmation">
            <FileText className="h-3.5 w-3.5" />{bt("confirmation")}
          </button>
          <StatusBadge status={process.status} />
        </div>
      </div>

      {/* Progress */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
        <ProgressBar status={process.status} />
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <DocumentVault documents={documents} onChange={setDocuments} />
          <MessageThread
            title="Messages"
            currentUserId={user?.id}
            listFn={() => getMyMessages(id)}
            sendFn={(text, file) => sendMyMessageFile(id, text, file)}
            markReadFn={() => markAccountRead(id)}
          />
        </div>

        {/* Timeline */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="customer-timeline">
          <h2 className="font-semibold mb-4">{t("account.detail.timeline")}</h2>
          {audit_logs.length === 0 && <p className="text-sm text-slate-400">{t("account.detail.noTimeline")}</p>}
          <ol className="relative border-l border-slate-800 ml-1 space-y-5">
            {audit_logs.map((a) => (
              <li key={a.id} className="ml-4" data-testid={`timeline-${a.id}`}>
                <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-venka-green-light border border-slate-950" />
                {a.new_status && (
                  <div className="mb-1">
                    <StatusBadge status={a.new_status} />
                  </div>
                )}
                {a.comment && (
                  <p className="text-sm text-slate-200 flex items-start gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5 text-slate-500 mt-0.5 shrink-0" />
                    {a.comment}
                  </p>
                )}
                <p className="text-[11px] text-slate-500 mt-1">
                  {a.actor_role && a.actor_role !== "customer" ? `${t("account.detail.agentUpdate")} · ` : ""}
                  {formatDateTime(a.timestamp, i18n.resolvedLanguage)}
                </p>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
};

export default CustomerProcessDetail;
